import React from 'react'
import { css } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import cookies from 'js-cookie'
import { saveAs } from 'file-saver'
import { decorateFetch } from '../../../api'
import { logError, logSuccess } from '../../toaster'
import { WithTooltip } from '../../../design-system'
import { I18nConsumer } from '../../i18n-provider'
import { linkCss } from '../../../legacy-design-system/paragraph/truncated-list-paragraph'
import { Link } from '@contrast-security-inc/design-system-typography'
import { theme } from '@contrast-security-inc/design-system-foundations'

export interface DownloadAttackValueLinkProps {
  readonly organizationID: string
  readonly eventUuid: string
  readonly showTooltip?: boolean
}

export const downloadFullValueTestID = 'download-full-value-test-id'

const buildAttackValueDownloadURL = (
  organizationID: string,
  eventUuid: string
) => `/Contrast/api/ng/${organizationID}/rasp/events/${eventUuid}/value`

const parse = (str: string) => {
  str = str.slice('attachment; filename="'.length - 1)
  return str.slice(0, str.length)
}

const decoratedFetch = decorateFetch(
  fetch,
  cookies,
  document.location,
  window.localStorage
)

const downloadAttackValue = async (intl, organizationID, eventUuid) => {
  const response = await decoratedFetch(
    buildAttackValueDownloadURL(organizationID, eventUuid),
    {
      method: 'POST'
    }
  )

  const errMsg = intl.formatMessage({
    id: 'detail_errorDownloadingAttackValue'
  })

  let filename = 'download'

  let blob: Blob
  try {
    blob = await response.blob()
  } catch (e) {
    logError(errMsg)
    return
  }

  if (response) {
    if (!response.ok || response.status >= 400) {
      logError(errMsg)
      return
    }
  }

  const contentDisposition = response.headers.get('content-disposition')

  if (contentDisposition) {
    filename = parse(contentDisposition)
  }

  try {
    saveAs(blob, filename)
    logSuccess(
      intl.formatMessage({
        id: 'detail_successfullyDownloadedAttackValue'
      })
    )
  } catch (e) {
    logError(errMsg)
    return
  }
}

const tooltipLinkCss = css`
  color: ${theme.color.turquoise50} !important;
`

export const DownloadAttackValueLink: React.FC<
  DownloadAttackValueLinkProps
> = ({ organizationID, eventUuid, showTooltip = false }) => {
  const LinkComponent = () => (
    <I18nConsumer>
      {({ intl }) => (
        <FormattedMessage
          id="action_attacks_downloadFullValue"
          children={(message) => (
            <div
              data-testid={downloadFullValueTestID}
              className={css`
                padding-top: 1px;
              `}
            >
              <Link
                className={showTooltip ? linkCss : tooltipLinkCss}
                onClick={() =>
                  downloadAttackValue(intl, organizationID, eventUuid)
                }
              >
                {message}
              </Link>
            </div>
          )}
        />
      )}
    </I18nConsumer>
  )

  if (showTooltip) {
    return (
      <WithTooltip
        placement="top"
        width="fit-content"
        renderTooltipContent={() => (
          <FormattedMessage id="detail_attackValueTooLarge" />
        )}
        children={() => <LinkComponent />}
      />
    )
  }

  return <LinkComponent />
}
